import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { createQuoteRequest, buildQuoteDescription, errorMessage } from "@/lib/drivego";
import HoneypotField from "@/components/HoneypotField";
import { toast } from "sonner";
import { CheckCircle, Loader2, FileText } from "lucide-react";

const inputCls = "mt-1 bg-white/[0.05] border-white/10 text-white placeholder:text-[#52525B] rounded-sm focus:border-[#00F0FF]";

const ServiceQuoteButton = ({ service, className = "", variant = "primary" }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    postal_code: "",
    customer_name: "",
    license_plate: "",
    mileage: "",
    phone: "",
    email: "",
    comments: "",
    website: "", // honeypot
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "postal_code") {
      setForm({ ...form, postal_code: value.replace(/\D/g, "").slice(0, 4) });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.postal_code.length !== 4) {
      toast.error("Postnummer skal være 4 cifre");
      return;
    }
    setSubmitting(true);
    try {
      await createQuoteRequest({
        description: buildQuoteDescription([
          ["Tjeneste", service.name],
          ["Postnummer", form.postal_code],
          ["Kilometerstand", form.mileage],
          ["Kommentar", form.comments],
        ]),
        customer: {
          name: form.customer_name,
          phone: form.phone,
          email: form.email,
          postalCode: form.postal_code,
        },
        licensePlate: form.license_plate,
        website: form.website,
      });
      setSuccess(true);
      toast.success("Din forespørgsel er sendt!");
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setDialogOpen(false);
    setTimeout(() => {
      setSuccess(false);
      setForm({ postal_code: "", customer_name: "", license_plate: "", mileage: "", phone: "", email: "", comments: "", website: "" });
    }, 300);
  };

  const baseClass =
    variant === "primary"
      ? "bg-[#00F0FF] hover:bg-[#00D0E0] text-black font-bold rounded-full glow-cyan"
      : "border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded-full bg-transparent font-bold";

  return (
    <>
      <Button
        onClick={(e) => {
          e.stopPropagation();
          setDialogOpen(true);
        }}
        className={`${baseClass} ${className}`}
        data-testid={`request-quote-btn-${service.id}`}
      >
        <FileText className="w-4 h-4 mr-2" /> Modtag tilbud
      </Button>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => !submitting && (open ? setDialogOpen(true) : resetAndClose())}
      >
        <DialogContent
          className="bg-[#0A0A0A] border border-white/10 text-white rounded-sm max-w-lg max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {success ? (
            <div className="py-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-[#34C759]/20 rounded-full">
                <CheckCircle className="w-10 h-10 text-[#34C759]" />
              </div>
              <h2 className="text-2xl font-black text-white mb-2" style={{ fontFamily: "Saira" }}>
                TAK FOR DIN FORESPØRGSEL!
              </h2>
              <p className="text-[#A0AEC0] mb-6">
                Vi har modtaget din anmodning og vender tilbage inden for 2 timer med et personligt tilbud.
              </p>
              <Button onClick={resetAndClose} className="bg-[#00F0FF] hover:bg-[#00D0E0] text-black font-bold rounded-full px-8 glow-cyan">
                Luk
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-black text-white" style={{ fontFamily: "Saira" }}>
                  ANMOD OM TILBUD
                </DialogTitle>
                <DialogDescription className="text-[#A0AEC0]">
                  Tjeneste: <span className="text-white font-bold">{service.name}</span>
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4 relative">
                <HoneypotField value={form.website} onChange={(v) => setForm({ ...form, website: v })} />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white">Navn *</Label>
                    <Input name="customer_name" value={form.customer_name} onChange={handleChange} required placeholder="Anders Jensen" className={inputCls} />
                  </div>
                  <div>
                    <Label className="text-white">Postnummer *</Label>
                    <Input name="postal_code" inputMode="numeric" maxLength={4} value={form.postal_code} onChange={handleChange} required placeholder="2100" className={inputCls} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white">Nummerplade *</Label>
                    <Input name="license_plate" value={form.license_plate} onChange={handleChange} required placeholder="AB 12 345" className={inputCls} />
                  </div>
                  <div>
                    <Label className="text-white">Kilometerstand *</Label>
                    <Input name="mileage" value={form.mileage} onChange={handleChange} required placeholder="120.000" className={inputCls} />
                  </div>
                </div>
                <div>
                  <Label className="text-white">Telefonnummer *</Label>
                  <Input name="phone" value={form.phone} onChange={handleChange} required placeholder="+45 12 34 56 78" className={inputCls} />
                </div>
                <div>
                  <Label className="text-white">Email *</Label>
                  <Input name="email" type="email" value={form.email} onChange={handleChange} required placeholder="anders@example.com" className={inputCls} />
                </div>
                <div>
                  <Label className="text-white">Evt. Kommentarer</Label>
                  <Textarea name="comments" value={form.comments} onChange={handleChange} placeholder="Beskriv gerne problemet eller særlige ønsker..." className={`${inputCls} min-h-[80px]`} />
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={resetAndClose} disabled={submitting} className="flex-1 border-white/15 bg-transparent text-white hover:bg-white/10 hover:text-white rounded-sm">
                    Annuller
                  </Button>
                  <Button type="submit" disabled={submitting} className="flex-1 bg-[#00F0FF] hover:bg-[#00D0E0] text-black font-bold rounded-sm glow-cyan">
                    {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sender...</>) : "Send forespørgsel"}
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ServiceQuoteButton;
